import type { ValidationRuleWithoutParams } from "@vuelidate/core";
import {
	required,
	email,
	sameAs as sameAsOrigin,
	minLength as minLengthOrigin,
	maxLength as maxLengthOrigin,
	numeric,
	requiredIf as requiredIfOrigin
} from "@vuelidate/validators";

required.$message = "Dit veld is verplicht";
email.$message = "Dit is geen geldig e-mailadres";
const sameAs = (val: any) => {
	const validator = sameAsOrigin(val);
	validator.$message = () => "De velden komen niet overeen";

	return validator;
};
const minLength = (val: any) => {
	const validator = minLengthOrigin(val);
	validator.$message = () => `Dit veld moet minimaal ${val} tekens bevatten`;

	return validator;
};
const maxLength = (val: any) => {
	const validator = maxLengthOrigin(val);
	validator.$message = () => `Dit veld mag maximaal ${val} tekens bevatten`;

	return validator;
};
numeric.$message = "Dit veld mag alleen cijfers bevatten";

const requiredIf = (val: any) => {
	const validator = requiredIfOrigin(val);
	validator.$message = () => "Dit veld is verplicht";

	return validator;
};

const postcode = (pattern: Ref<RegExp> | RegExp) => {
	return {
		$message: "Dit is geen geldige postcode",
		$validator: (value: string) => {
			return value.match(unref(pattern)) !== null;
		}
	};
};

const bancontactNumber = {
	$message: "Dit is geen geldig Bancontact kaartnummer",
	$validator: (value: string) => {
		// Accept only digits.
		if (/[^0-9]+/.test(value))
			return false;
		// Accept only card numbers with a length between 10 and 19.
		if (value.length < 10 || value.length > 19)
			return false;
		// The Luhn Algorithm.
		var sum = 0;
		for (var i = 0; i < value.length; i++) {
			var digit = parseInt(value.charAt(i), 10);
			if (i % 2 === value.length % 2) {
				digit *= 2;
				if (digit > 9) {
					digit -= 9;
				}
			}
			sum += digit;
		}
		if (sum % 10 !== 0){
			return false;
		}
		return /^(4544|4579|4796|4871|4918|4934|4940|5127|5169|5182|5203|5214|5221|5229|5244|5247|5255|5394|5613|5614|6060|6703|4810|5480|5559)[0-9]{12,15}$/.test(value);
	}
}

const creditcardNumber = {
	$message: "Dit is geen geldig kaartnummer, of het kaartnummer is niet van een verstrekker die wij accepteren.",
	$validator: (value: string) => {
		//remove spaces
		value = value.replace(/\s/g, "");
		// Accept only digits.
		if (/[^0-9]+/.test(value))
			return false;
		// Accept only card numbers with a length between 10 and 19.
		if (value.length < 10 || value.length > 19){
			return false;
		}
		// The Luhn Algorithm.
		var sum = 0;
		for (var i = 0; i < value.length; i++) {
			var digit = parseInt(value.charAt(i), 10);
			if (i % 2 === value.length % 2) {
				digit *= 2;
				if (digit > 9) {
					digit -= 9;
				}
			}
			sum += digit;
		}
		if (sum % 10 !== 0){
			return false;
		}

		// VISA, Maestro, Mastercard regex tests
		return /^4[0-9]{12}(?:[0-9]{3})?$/.test(value) || /^(5[1-5]|2[2-7])[0-9]{14}$/.test(value) || /^\d{12,19}$/.test(value);
	}
}

const expiry = {
	$message: "Dit is geen geldige datum",
	$validator: (value: string) => {
		if(!/^(0[1-9]|1[0-2])\/?([0-9]{2})$/.test(value)){
			return false
		}

		let month = parseInt(value.split("/")[0]);
		let year = parseInt("20" + value.split("/")[1]);

		if(month == 12){
			month = 1;
			year += 1;
		}

		const today = new Date();
		const lastDayOfGivenExpiration = new Date(year, month, 0);
		const lastDayofCurrentMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)

		if(lastDayOfGivenExpiration < lastDayofCurrentMonth){
			return false
		}

		return true
	}
}

const onlyIf = (condition: Ref<boolean>, ...validators: ValidationRuleWithoutParams[]) => {
	const randomKey = (Math.random() + 1).toString(36).substring(7);
	return validators.reduce((acc, validator, index) => {
		return {
			...acc,
			[`$onlyIf_${randomKey}_${index}`]: {
				...validator,
				$validator: (value: any, siblingState: any, vm: any) => {
					return unref(condition) ? validator.$validator(value, siblingState, vm) : true;
				}
			}
		};
	}, {});
};

const firstOrLastName = {
	$message: "Dit is geen geldige naam",
	$validator: (value: string) => {
		const regex = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/;
		return regex.test(value);
	}
};

const businessName = {
	$message: "Dit is geen geldige bedrijfsnaam",
	$validator: (value: string) => {
		const regex = /^[A-Za-z0-9À-ÖØ-öø-ÿ'&., -+@!*#/()]+$/;
		return regex.test(value);
	}
};

const phoneNumber = {
	$message: "Dit is geen geldig telefoonnummer",
	$validator: (value: string) => {
		return value.match(/^\+?[0-9]{1,4}?[0-9 \-()]{7,16}$/) !== null;
	}
};

const url = {
	$message: "Dit is geen geldige URL",
	$validator: (value: string) => {
		return value.match(/^(https?:\/\/)?([\w\d\-_]+\.)?[\w\d\-_]+\.\w{2,}(\/.*)?$/) !== null;
	}
};

const iban = {
	$message: "Ongeldige iBAN",
	$validator: (value: string) => {
		return isValidIBANNumber(value);
	}
}

function isValidIBANNumber(input: string) {
    const CODE_LENGTHS = {
        BE: 16, DE: 22, NL: 18
    };
    var iban = String(input).toUpperCase().replace(/[^A-Z0-9]/g, ''), // keep only alphanumeric characters
            code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/), // match and capture (1) the country code, (2) the check digits, and (3) the rest
            digits;
    // check syntax and length
    if (!code || iban.length !== CODE_LENGTHS[code[1]]) {
        return false;
    }
    // rearrange country code and check digits, and convert chars to ints
    digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, (letter) => {
        return letter.charCodeAt(0) - 55;
    });
    // final check
    return mod97(digits) === 1;
}

function mod97(iban: any) {
    var checksum = iban.slice(0, 2), fragment;
    for (var offset = 2; offset < iban.length; offset += 7) {
        fragment = String(checksum) + iban.substring(offset, offset + 7);
        checksum = parseInt(fragment, 10) % 97;
    }
    return checksum;
}

export {
	required,
	email,
	sameAs,
	minLength,
	numeric,
	requiredIf,
	postcode,
	phoneNumber,
	maxLength,
	firstOrLastName,
	iban,
	businessName,
	onlyIf,
	url,
	bancontactNumber,
	creditcardNumber,
	expiry
};
